/**
 * lesson-plans/$id/index.tsx — Edit an existing lesson plan.
 *
 * Loads the plan via lessonPlanQuery(id) and renders the builder in
 * edit mode. Mutations on items/title/description hit the existing
 * lesson-plans API (Task 23). A "Lihat versi cetak" link in the page
 * header jumps to the A4 print view at `/lesson-plans/$id/print`.
 */

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { LessonPlanBuilder } from '../../../components/lessons/LessonPlanBuilder.js';
import { freshSessionQuery, lessonPlanQuery } from '../../../lib/api/queries.js';

export const Route = createFileRoute('/lesson-plans/$id/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: EditLessonPlanPage,
});

function EditLessonPlanPage() {
  const { id } = Route.useParams();
  return <LessonPlanEditGate id={id} />;
}

/** Exported for tests: resolves the plan and renders not-found / builder. */
export function LessonPlanEditGate({ id }: { id: string }) {
  // Audit F10: an unknown / foreign plan id used to open an empty "tanpa judul"
  // editor. Resolve the plan first and show a proper not-found state.
  const plan = useQuery(lessonPlanQuery(id));

  if (plan.isLoading) {
    return (
      <main className="page-shell">
        <StatePanel variant="empty" title="Memuat lesson plan…" />
      </main>
    );
  }
  if (plan.data && !plan.data.ok) {
    const forbidden = plan.data.error.code === 'forbidden';
    return (
      <main className="page-shell" data-testid="lesson-plan-not-found">
        <StatePanel
          variant="error"
          title={forbidden ? 'Lesson plan ini bukan milikmu' : 'Lesson plan tidak ditemukan'}
          description="Mungkin sudah dihapus, atau tautannya salah. Kembali ke daftar lesson plan untuk memilih yang lain."
        />
        <p>
          <Link to="/lesson-plans" className="button button-secondary">
            Kembali ke daftar
          </Link>
        </p>
      </main>
    );
  }
  return <LessonPlanBuilder planId={id} />;
}